<template>
  <div class="chat-widget">
    <!-- 图一：初始按钮 -->
    <div v-if="currentStep === 'button'" class="chat-button" @click="showWelcome">
      <div class="chat-button-content">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
            fill="currentColor" />
          <path d="M7 9H17V11H7V9Z" fill="currentColor" />
          <path d="M7 12H14V14H7V12Z" fill="currentColor" />
        </svg>
        <span>{{ $t('chatWidget.chatWithUs') }}</span>
      </div>
    </div>

    <!-- 图二：欢迎弹框 -->
    <div v-if="currentStep === 'welcome'" class="chat-popup">
      <div class="chat-popup-header">
        <button class="close-button" @click="closeChat">×</button>
      </div>
      <div class="chat-popup-content">
        <div class="avatar-section">
          <div class="avatar">
            <img src="@/assets/image/avatar-icon.png" alt="Avatar" />
          </div>
        </div>
        <h3 class="welcome-title">{{ $t('chatWidget.welcomeTitle') }}</h3>
        <p class="welcome-message">{{ $t('chatWidget.welcomeMessage') }}</p>
        <button class="continue-btn" @click="handleContinueClick">
          {{ hasUserInfo ? $t('chatWidget.continueChat') : $t('chatWidget.startConversation') }}
        </button>
      </div>
    </div>

    <!-- 图三：信息填写表单 -->
    <div v-if="currentStep === 'form'" class="chat-popup chat-popup-form">
      <div class="chat-popup-header">
        <button class="close-button" @click="closeChat">×</button>
      </div>
      <div class="chat-popup-content">
        <h3 class="form-title">{{ $t('chatWidget.formTitle') }}</h3>
        <el-form :model="formData" :rules="formRules" ref="chatForm" class="chat-form">
          <el-form-item prop="name">
            <el-input v-model="formData.name" :placeholder="$t('chatWidget.namePlaceholder')" />
          </el-form-item>
          <el-form-item prop="email">
            <el-input v-model="formData.email" :placeholder="$t('chatWidget.emailPlaceholder')" />
          </el-form-item>
          <el-form-item prop="phone">
            <el-input v-model="formData.phone" :placeholder="$t('chatWidget.phonePlaceholder')" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" class="submit-btn" @click="submitForm" :loading="submitting">
              {{ $t('chatWidget.startChat') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 图四：聊天对话框 -->
    <div v-if="currentStep === 'chat'" class="chat-popup chat-popup-chat">
      <div class="chat-popup-header">
        <div class="chat-header-info">
          <div class="avatar-small">
            <img src="@/assets/image/avatar-icon.png" alt="Avatar" />
          </div>
          <span class="chat-title">{{ isCSAssigned ? $t('chatWidget.chatTitle') : $t('chatWidget.chatTitleNoAgent') }}</span>
        </div>
        <div class="header-actions">
          <button class="end-chat-button" @click="showRateDialog" v-if="!sessionEnded">
            {{ $t('chatWidget.endChat') || '结束会话' }}
          </button>
          <button class="close-button" @click="closeChat">×</button>
        </div>
      </div>
      <div class="chat-messages" ref="messageContainer">
        <div v-for="(message, index) in messages" :key="index" :class="['message', message.type]">
          <div class="message-content">
            <div v-if="message.type === 'agent'" class="avatar-message">
              <img src="@/assets/image/avatar-icon.png" alt="Avatar" />
            </div>
            <div class="message-bubble common_chat_emoji-wrapper-global">
              <!-- 时间戳和发送者 -->
              <p class="message-time" v-if="message.type === 'agent'">
                <span>客服</span> {{ formatTimestamp(message.timestamp) }}
              </p>
              <!-- 文本消息（支持emoji） -->
              <div v-if="message.text && !message.imageUrl" v-html="getqqemojiEmoji(message.text)"></div>
              <!-- 图片消息 -->
              <div v-if="message.imageUrl" class="image-message">
                <el-image :src="message.imageUrl" :preview-src-list="[message.imageUrl]" fit="cover"
                  class="message-image">
                  <div slot="error" class="image-error">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-image>
              </div>
            </div>
          </div>
        </div>
        <div v-if="typing" class="message agent">
          <div class="message-content">
            <div class="avatar-message">
              <img src="@/assets/image/avatar-icon.png" alt="Avatar" />
            </div>
            <div class="message-bubble typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
      <div class="chat-input-area" :class="{ 'session-ended': sessionEnded }">
        <!-- 操作按钮：表情和附件 -->
        <div class="input-actions">
          <!-- 表情选择器 -->
          <common-chat-emoji @select="handleEmojiSelect" :disabled="sessionEnded"></common-chat-emoji>

          <el-tooltip :content="$t('chatWidget.uploadImage')" placement="top" effect="dark">
            <div class="action-btn attachment-btn" @click="triggerFileUpload" :class="{ 'disabled': sessionEnded }">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                  fill="currentColor" />
              </svg>
            </div>
          </el-tooltip>
          <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="handleFileUpload" />
        </div>

        <el-input
          v-model="newMessage"
          :placeholder="sessionEnded ? ($t('chatWidget.sessionEnded') || '会话已结束') : $t('chatWidget.inputPlaceholder')"
          @keyup.enter.native="sendMessage"
          :disabled="sessionEnded"
        />
      </div>
    </div>

    <!-- 评价弹窗 -->
    <im-rate
      v-if="rateVisible"
      :group-id="globalUserId"
      :data-item="rateData"
      @custom-event="handleRatingSubmit"
      @end-chat="handleRatingEnd"
      @back-chat="handleRatingCancel"
    />
  </div>
</template>

<script>
import { assignCS, uploadImg, historyList } from '@/common/imCilent';
import commonChatEmoji from '@/components/common/common_chat_emoji.vue';
import ImRate from './imRate.vue';

export default {
  name: 'ChatWidget',
  components: {
    commonChatEmoji,
    ImRate
  },
  data() {
    return {
      currentStep: 'button', // button, welcome, form, chat
      formData: {
        name: '',
        email: '',
        phone: ''
      },
      submitting: false,
      messages: [],
      newMessage: '',
      typing: false,
      // WebSocket相关
      socket: null,
      globalDomainUrl: 'www.tinggo.com',
      globalUserId: null,
      groupId: null,
      isConnected: false,
      csUserId: null, // 客服ID
      isCSAssigned: false, // 是否已分配到客服
      // 历史消息相关
      total: 0, // 历史消息总数
      historyDataList: [], // 历史消息集合
      datas: {
        messageTo: null,
        globalUserId: null,
        pageNum: 1,
        pageSize: 20,
      },
      isLoading: false, // 是否正在加载
      // 评价相关
      rateVisible: false, // 评价弹窗是否可见
      sessionEnded: false, // 会话是否已结束
      rateData: {
        rate: null,
        remark: '',
        groupUniqueId: '',
        reply: 1,
        imMessageSessionId: ''
      }
    };
  },
  computed: {
    // 检查是否有保存的用户信息
    hasUserInfo() {
      return !!(this.formData.name && this.formData.email && this.formData.phone);
    },
    // 动态生成表单验证规则（支持国际化）
    formRules() {
      return {
        name: [
          { required: true, message: this.$t('chatWidget.nameRequired'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.$t('chatWidget.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('chatWidget.emailInvalid'), trigger: 'blur' }
        ],
        phone: [
          { required: true, message: this.$t('chatWidget.phoneRequired'), trigger: 'blur' }
        ]
      };
    }
  },
  watch: {
    currentStep(newVal, oldVal) {
      console.log('currentStep 变化:', oldVal, '->', newVal);
      // 通知父页面调整 iframe 大小
      this.notifyParentResize();
    }
  },
  mounted() {
    console.log('组件已挂载，当前步骤:', this.currentStep);

    // 初始化语言设置（从 localStorage 读取）
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      const normalizedLang = this.normalizeLanguage(savedLanguage);
      console.log('初始化语言设置:', normalizedLang);
      this.$i18n.locale = normalizedLang;
      localStorage.setItem('language', normalizedLang);
    } else {
      // 如果没有保存的语言，使用默认值
      const defaultLang = 'en-US';
      console.log('使用默认语言:', defaultLang);
      this.$i18n.locale = defaultLang;
      localStorage.setItem('language', defaultLang);
    }

    // 检查是否有保存的用户信息
    this.loadUserInfo();
    console.log('加载用户信息后的 formData:', this.formData);

    // 监听来自父页面的消息
    window.addEventListener('message', this.handleMessage);

    // 主动向父页面请求语言设置（确保语言同步）
    this.$nextTick(() => {
      window.parent.postMessage({
        type: 'requestLanguage'
      }, '*');

      // 通知父页面当前状态，确保 iframe 尺寸正确
      this.notifyParentResize();
    });
  },
  beforeDestroy() {
    // 组件销毁时断开WebSocket连接
    if (this.socket) {
      this.socket.disconnect();
    }
    // 移除消息监听
    window.removeEventListener('message', this.handleMessage);
  },
  methods: {
    // 统一语言格式的方法
    normalizeLanguage(language) {
      if (!language) return 'en-US';

      // 支持的语言映射表
      const languageMap = {
        'en': 'en-US',
        'zh': 'zh-CN',
        'zh-CN': 'zh-CN',
        'zh-TW': 'zh-TW',
        'ja': 'ja-JP',
        'ja-JP': 'ja-JP',
        'ko': 'ko-KR',
        'ko-KR': 'ko-KR',
        'es': 'es-ES',
        'es-ES': 'es-ES',
        'fr': 'fr-FR',
        'fr-FR': 'fr-FR',
        'de': 'de-DE',
        'de-DE': 'de-DE',
        'it': 'it-IT',
        'it-IT': 'it-IT',
        'no': 'no-NO',
        'no-NO': 'no-NO',
        'nb': 'no-NO',
        'nb-NO': 'no-NO'
      };

      // 查找映射表中的语言，如果找到则返回标准格式
      if (languageMap[language]) {
        return languageMap[language];
      }

      // 如果已经是完整格式（包含-），直接返回
      if (language.includes('-')) {
        return language;
      }

      // 默认返回英语
      return 'en-US';
    },
    // 通知父页面调整 iframe 大小
    notifyParentResize() {
      // 如果是 button 状态，使用小尺寸
      if (this.currentStep === 'button') {
        window.parent.postMessage({
          type: 'resizeIframe',
          width: 220,
          height: 100
        }, '*');
        console.log('notifyParentResize resizeIframe 220 100');
      } else {
        // 其他所有状态（welcome, form, chat）都使用大尺寸
        window.parent.postMessage({
          type: 'resizeIframe',
          width: 400,
          height: 520
        }, '*');
        console.log('notifyParentResize resizeIframe 400 520');
      }
    },
    // 处理来自父页面的消息
    handleMessage(event) {
      console.log('imTinggo 收到父页面消息:', event.data);

      const loginMsg = event.data;

      // 处理 showWelcome 消息
      if (loginMsg && loginMsg.type === 'showWelcome') {
        console.log('收到 showWelcome 消息');
        // 设置语言
        if (loginMsg.tmschat_language) {
          const normalizedLang = this.normalizeLanguage(loginMsg.tmschat_language);
          console.log('设置语言:', normalizedLang);
          this.$i18n.locale = normalizedLang;
          localStorage.setItem('language', normalizedLang);
        }
        // 设置用户信息（如果提供）
        if (loginMsg.tmschat_user_name) {
          this.formData.name = loginMsg.tmschat_user_name;
        }
        if (loginMsg.tmschat_user_id) {
          this.formData.email = loginMsg.tmschat_user_id;
        }
        // 调用 showWelcome 方法
        this.showWelcome();
        return;
      }

      // 处理语言响应
      if (loginMsg && loginMsg.type === 'language' && loginMsg.language) {
        const normalizedLang = this.normalizeLanguage(loginMsg.language);
        console.log('收到语言响应:', normalizedLang);
        this.$i18n.locale = normalizedLang;
        localStorage.setItem('language', normalizedLang);
        return;
      }

      // 处理语言设置（从 sendMessageToIframe 传递的）
      if (loginMsg && loginMsg.tmschat_language) {
        const normalizedLang = this.normalizeLanguage(loginMsg.tmschat_language);
        console.log('设置语言:', normalizedLang);
        this.$i18n.locale = normalizedLang;
        localStorage.setItem('language', normalizedLang);
      }

      // 保存用户信息（如果父页面传递了用户信息）
      if (loginMsg && loginMsg.tmschat_user_name) {
        this.formData.name = loginMsg.tmschat_user_name;
      }
      if (loginMsg && loginMsg.tmschat_user_id) {
        this.formData.email = loginMsg.tmschat_user_id;
      }

      // 如果需要显示聊天框且用户信息完整
      if (loginMsg && loginMsg.tmschat_show_dialog === true) {
        // 检查是否已连接
        if (this.socket && this.socket.connected && this.isConnected) {
          this.currentStep = 'chat';
        } else if (this.hasUserInfo) {
          // 有用户信息，初始化连接
          this.submitting = true;
          this.initWebSocket();
        } else {
          // 没有完整用户信息，显示欢迎页
          this.currentStep = 'welcome';
        }
        // watcher 会自动通知父页面调整 iframe 大小
      }
    },
    // 转换为QQ表情（从 [微笑] 转换为 img 标签）
    getqqemojiEmoji(value) {
      if (value == undefined) {
        return '';
      }
      const qqemojiList = ['微笑', '撇嘴', '色', '发呆', '得意', '流泪', '害羞', '闭嘴', '睡', '大哭', '尴尬', '发怒', '调皮', '呲牙', '惊讶', '难过', '酷', '冷汗', '抓狂', '吐', '偷笑', '愉快', '白眼', '傲慢', '饥饿', '困', '惊恐', '流汗', '憨笑', '悠闲', '奋斗', '咒骂', '疑问', '嘘', '晕', '疯了', '衰', '骷髅', '敲打', '再见', '擦汗', '抠鼻', '鼓掌', '糗大了', '坏笑', '左哼哼', '右哼哼', '哈欠', '鄙视', '委屈', '快哭了', '阴险', '亲亲', '吓', '可怜', '菜刀', '西瓜', '啤酒', '篮球', '乒乓', '咖啡', '饭', '猪头', '玫瑰', '凋谢', '嘴唇', '爱心', '心碎', '蛋糕', '闪电', '炸弹', '刀', '足球', '瓢虫', '便便', '月亮', '太阳', '礼物', '拥抱', '强', '弱', '握手', '胜利', '抱拳', '勾引', '拳头', '差劲', '爱你', 'NO', 'OK', '爱情', '飞吻', '跳跳', '发抖', '怄火', '转圈', '磕头', '回头', '跳绳', '投降', '激动', '乱舞', '献吻', '左太极', '右太极'];

      let rs = value.replace(/\[(.+?)\]/g, function (item, value) {
        let faceIndex = qqemojiList.indexOf(value);
        if (faceIndex !== -1) {
          return `<img class="qqemoji small qqemoji${faceIndex}" text="${value}" src="/static/image/im_emoji_spacer.gif"/>`;
        }
        return item;
      });
      return rs;
    },
    // 从 cookie 加载用户信息
    loadUserInfo() {
      const savedUserInfo = this.getCookie('tinggo_chat_user_info');
      if (savedUserInfo) {
        try {
          console.log('从 cookie 读取到的用户信息:', savedUserInfo);
          const userInfo = JSON.parse(savedUserInfo);
          console.log('解析后的用户信息:', userInfo);
          this.formData = {
            name: userInfo.name || '',
            email: userInfo.email || '',
            phone: userInfo.phone || ''
          };
          this.globalUserId = this.globalDomainUrl + '_' + this.formData.email;
          console.log('设置后的 formData:', this.formData);
          console.log('设置后的 globalUserId:', this.globalUserId);
        } catch (e) {
          console.error('加载用户信息失败:', e);
          console.error('错误的 cookie 值:', savedUserInfo);
          // 清除损坏的 cookie
          this.deleteCookie('tinggo_chat_user_info');
        }
      } else {
        console.log('cookie 中没有找到用户信息');
      }
    },
    // 保存用户信息到 cookie
    saveUserInfo() {
      const userInfo = {
        name: this.formData.name,
        email: this.formData.email,
        phone: this.formData.phone
      };
      const userInfoStr = JSON.stringify(userInfo);
      console.log('准备保存到 cookie 的用户信息:', userInfoStr);
      // 设置cookie，有效期30天
      this.setCookie('tinggo_chat_user_info', userInfoStr, 30);
      console.log('cookie 保存完成');
    },
    // 设置cookie
    setCookie(name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
      }
      // 使用 encodeURIComponent 对值进行编码，避免特殊字符问题
      document.cookie = name + '=' + (encodeURIComponent(value) || '') + expires + '; path=/';
    },
    // 获取cookie
    getCookie(name) {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
          // 使用 decodeURIComponent 解码
          return decodeURIComponent(c.substring(nameEQ.length, c.length));
        }
      }
      return null;
    },
    // 删除cookie
    deleteCookie(name) {
      document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    },
    // 转换历史消息格式
    convertMessages(messages) {
      return messages.map(message => {
        const role = message.senderType === 1 ? 'agent' : 'user'; // 1为客服(agent)，0为用户(user)
        return {
          contentType: message.contentType,
          content: message.messageContent,
          text: message.messageContent,
          role: role,
          type: role,
          createTime: new Date(message.createTime).toISOString(),
          timestamp: message.sendTime,
          requestId: message.messageRequestId,
          ackState: false,
          retryCount: 0,
          isSuccess: true,
          referenceContent: message.referenceContent ? JSON.parse(message.referenceContent) : {},
          fromName: message.nickName,
          messageExpand: message.messageExpand ? JSON.parse(message.messageExpand) : {}
        };
      });
    },
    // 加载历史聊天记录
    loadChatHistory() {
      if (!this.globalUserId || !this.groupId) {
        console.warn('无法加载历史记录：缺少 globalUserId 或 groupId');
        return;
      }

      let datas = {
        messageTo: this.groupId,
        globalUserId: this.globalUserId,
        pageNum: this.datas.pageNum,
        pageSize: this.datas.pageSize,
      };

      historyList(datas).then((res) => {
        console.log('获取历史消息成功:', res);
        this.total = res.total || 0;
        let row = res.rows || [];
        this.historyDataList = this.convertMessages(row).reverse();

        // 将历史消息添加到当前消息列表
        if (this.historyDataList.length > 0) {
          // 转换历史消息为当前格式
          this.messages = this.historyDataList.map(msg => ({
            type: msg.role,
            text: msg.content,
            imageUrl: msg.contentType === 4 ? msg.content : null,
            contentType: msg.contentType,
            timestamp: msg.timestamp || msg.createTime || Date.now()
          }));
        }

        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }).catch(err => {
        console.error('加载历史记录失败:', err);
      });
    },
    // 清除用户信息
    clearUserInfo() {
      this.deleteCookie('tinggo_chat_user_info');
    },
    // 处理"Continue"按钮点击
    handleContinueClick() {
      // 重置会话状态
      this.sessionEnded = false;
      this.rateVisible = false;
      this.messages = [];

      if (this.hasUserInfo) {
        // 有用户信息，直接进入聊天或重新连接
        if (this.socket && this.socket.connected && this.isConnected && !this.sessionEnded) {
          this.currentStep = 'chat';
        } else {
          this.submitting = true;
          this.initWebSocket();
        }
      } else {
        // 没有用户信息，显示表单
        this.showForm();
      }
    },
    showWelcome() {
      // 检查是否有保存的用户信息
      const savedUserInfo = this.getCookie('tinggo_chat_user_info');
      console.log('showWelcome - 检查 cookie:', savedUserInfo);

      if (savedUserInfo) {
        try {
          const userInfo = JSON.parse(savedUserInfo);
          console.log('解析用户信息成功:', userInfo);

          // 检查用户信息是否完整
          if (userInfo.name && userInfo.email && userInfo.phone) {
            this.formData = {
              name: userInfo.name,
              email: userInfo.email,
              phone: userInfo.phone
            };

            // 重置会话状态
            this.sessionEnded = false;
            this.rateVisible = false;
            this.messages = [];

            // 有完整用户信息，直接进入聊天（如果已连接）或重新连接
            if (this.socket && this.socket.connected && this.isConnected && !this.sessionEnded) {
              this.currentStep = 'chat';
            } else {
              // 重新连接WebSocket
              this.submitting = true;
              this.initWebSocket();
            }
          } else {
            // 用户信息不完整，显示欢迎页
            console.log('用户信息不完整，显示欢迎页');
            this.currentStep = 'welcome';
          }
        } catch (e) {
          console.error('解析用户信息失败:', e);
          // 解析失败，显示欢迎页
          this.currentStep = 'welcome';
        }
      } else {
        // 没有保存的用户信息，显示欢迎页
        console.log('没有保存的用户信息，显示欢迎页');
        this.currentStep = 'welcome';
      }
    },
    showForm() {
      this.currentStep = 'form';
    },
    closeChat() {
      this.currentStep = 'button';
      // 关闭评价弹窗
      this.rateVisible = false;
      // 不断开WebSocket连接，保持在线状态
      // 重置聊天界面数据（不清空消息记录）
      this.newMessage = '';
    },
    submitForm() {
      this.$refs.chatForm.validate((valid) => {
        if (valid) {
          // 验证通过后，检查数据是否完整
          console.log('表单验证通过，formData:', this.formData);

          if (!this.formData.name || !this.formData.email || !this.formData.phone) {
            console.error('表单数据不完整:', this.formData);
            this.$message.error('请填写完整信息');
            return;
          }

          this.submitting = true;
          // 初始化WebSocket连接
          this.initWebSocket();
        } else {
          console.log('表单验证失败');
          return false;
        }
      });
    },
    // 生成唯一的requestId
    generateRequestId() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0,
          v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    // 初始化WebSocket连接
    initWebSocket() {
      if (this.socket && this.socket.connected) {
        this.submitting = false;
        this.currentStep = 'chat';
        return;
      }

      // 创建WebSocket连接
      const io = require('socket.io-client');
      this.socket = io(process.env.VUE_APP_WS_DOMAIN, {
        transports: ['websocket']
      });

      // 监听连接事件
      this.socket.on('connect', () => {
        console.log('WebSocket connected');
        this.login();
      });

      // 监听断开连接
      this.socket.on('disconnect', (err) => {
        console.error('WebSocket disconnect:', err);
        this.isConnected = false;
      });

      // 监听错误
      this.socket.on('error', (err) => {
        console.error('WebSocket error:', err);
        this.submitting = false;
        // this.$message.error('连接失败,请稍后重试');
      });

      // 监听连接错误
      this.socket.on('connect_error', (err) => {
        console.error('Connection error:', err);
        this.submitting = false;
        // this.$message.error('连接失败,请稍后重试');
      });

      // 接收服务端消息
      this.socket.on('receive', (data, ack) => {
        console.log('收到消息:', data);
        if (data.referenceContent) {
          try {
            data.referenceContent = JSON.parse(data.referenceContent);
          } catch (e) {
            console.error('解析referenceContent失败:', e);
          }
        }

        // 添加接收到的消息
        this.addAgentMessage(data.content, data.contentType);

        // 发送ACK确认
        const ackData = {
          from: this.globalUserId,
          groupId: data.groupId,
          content: data.content,
          contentType: data.contentType,
          requestId: this.generateRequestId(),
          timestamp: Date.now(),
          version: '1.0',
          globalUserId: this.globalUserId,
          code: 20000,
          message: 'ack',
          messageId: data.messageId
        };
        ack(ackData);
      });

      // 监听会话结束事件
      this.socket.on('session_end', (data, ack) => {
        console.log('会话结束:', data);
        // 标记会话已结束
        this.sessionEnded = true;
        // 显示评价弹窗
        this.rateVisible = true;
        this.rateData.imMessageSessionId = data.imMessageSessionId || '';
        // 发送 ACK 确认
        const ackData = {
          from: this.globalUserId,
          groupId: this.groupId || this.globalUserId,
          code: 20000,
          message: 'ack'
        };
        ack(ackData);
      });
    },
    // WebSocket登录
    login() {
      console.log('开始登录，当前 formData:', this.formData);
      console.log('当前 globalUserId:', this.globalUserId);

      // 生成全局用户ID (使用邮箱作为唯一标识)
      this.globalUserId = this.globalDomainUrl + '_' + this.formData.email;

      const loginData = {
        requestId: this.generateRequestId(),
        timestamp: Date.now(),
        version: '1.0',
        globalUserId: this.globalUserId,
        userName: this.formData.name,
        userId: this.formData.email,
        mobile: this.formData.phone,
        userType: 0, // 0表示客户端用户
        loginSystemName: 'tinggo',
        loginSystemDomain: this.globalDomainUrl
      };

      console.log('登录数据:', loginData);

      this.socket.emit('login', loginData, (res) => {
        console.log('登录响应:', res);
        if (res && res.code === 20000) {
          console.log('登录成功:', res);
          this.isConnected = true;
          this.submitting = false;

          // 保存客服ID
          if (res.csUserId) {
            this.csUserId = res.csUserId;
            this.isCSAssigned = true;
          }

          // 保存用户信息
          this.saveUserInfo();
          this.currentStep = 'chat';
          // 加载历史聊天记录
          this.loadChatHistory();

          // 只有在没有历史记录且已分配到客服时，才添加欢迎消息
          if (this.messages.length === 0 && this.isCSAssigned) {
            this.addAgentMessage(`${this.$t('chatWidget.greeting')} ${this.formData.name}! ${this.$t('chatWidget.greetingMessage')}`);
          }

          // 登录成功后调用分配客服接口
          this.assignCustomerService();
        } else {
          console.error('登录失败:', res);
          this.submitting = false;
          this.$message.error('登录失败,请稍后重试');
        }
      });
    },
    // 分配客服
    assignCustomerService() {
      const assignCSData = {
        globalUserId: this.globalUserId,
        loginSystemName: 'tinggo',
        loginSystemDomain: this.globalDomainUrl
      };

      // 调用接口分配客服
      assignCS(assignCSData).then(res => {
        console.log('分配客服成功:', res);
        if (res.code === 200 && res.data && res.data.csUserId) {
          this.csUserId = res.data.csUserId;
          this.isCSAssigned = true;

          // 如果之前没有添加欢迎消息且没有历史记录，现在添加
          if (this.messages.length === 0) {
            this.addAgentMessage(`${this.$t('chatWidget.greeting')} ${this.formData.name}! ${this.$t('chatWidget.greetingMessage')}`);
          }
        }
      }).catch(err => {
        console.error('分配客服失败:', err);
      });
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;

      // 检查会话是否已结束
      if (this.sessionEnded) {
        this.$message.warning(this.$t('chatWidget.sessionEnded') || '会话已结束，无法发送消息');
        return;
      }

      // 检查WebSocket连接状态
      if (!this.socket || !this.socket.connected) {
        this.$message.error(this.$t('chatWidget.connectionLost'));
        return;
      }

      const userMessage = this.newMessage;
      const requestId = this.generateRequestId();
      const timestamp = Date.now();

      // 添加用户消息到界面
      this.messages.push({
        type: 'user',
        text: userMessage,
        timestamp: timestamp
      });
      this.newMessage = '';

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // 通过WebSocket发送消息
      const messageData = {
        requestId: requestId,
        timestamp: timestamp,
        version: '1.0',
        globalUserId: this.globalUserId,
        from: this.formData.email,
        groupId: this.globalUserId, // 客户端使用自己的globalUserId作为groupId
        contentType: 0, // 0表示文本消息
        content: userMessage,
        referenceContent: JSON.stringify({})
      };

      this.socket.emit('send', messageData, (res) => {
        if (!res) {
          console.error('消息发送失败,超时');
          this.$message.error(this.$t('chatWidget.sendFailed'));
        } else {
          console.log('消息发送成功:', res);
        }
      });
    },
    // 检查是否为图片URL
    isImageUrl(url) {
      if (!url || typeof url !== 'string') return false;

      // 支持的图片格式
      const imageExtensions = [
        '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
        '.svg', '.ico', '.tif', '.tiff', '.psd', '.raw',
        '.JPG', '.JPEG', '.PNG', '.GIF', '.BMP', '.WEBP',
        '.SVG', '.ICO', '.TIF', '.TIFF', '.PSD', '.RAW'
      ];

      // 检查是否包含图片后缀
      const hasImageExtension = imageExtensions.some(ext => url.includes(ext));

      // 检查是否为常见图片托管服务的URL
      const imageUrlPatterns = [
        /https?:\/\/.*\.(jpg|jpeg|png|gif|bmp|webp|svg|ico|tif|tiff)(\?.*)?$/i,
        /https?:\/\/imgur\.com\/\w+/,
        /https?:\/\/i\.imgur\.com\/\w+/,
        /https?:\/\/.*\.amazonaws\.com\/.*\.(jpg|jpeg|png|gif|bmp|webp)(\?.*)?$/i,
        /https?:\/\/cloudinary\.com\/.*/,
        /https?:\/\/res\.cloudinary\.com\/.*/,
        /https?:\/\/.*\.cloudfront\.net\/.*/,
      ];

      const isImageUrl = imageUrlPatterns.some(pattern => pattern.test(url));

      return hasImageExtension || isImageUrl;
    },
    addAgentMessage(text, contentType = null) {
      const message = {
        type: 'agent',
        text: text,
        timestamp: Date.now()
      };

      // 检查是否为图片消息
      // 1. 优先检查 contentType (4 表示图片消息)
      if (contentType === 4) {
        message.imageUrl = text;
        message.text = '';
      }
      // 2. 如果 contentType 不是 4，检查内容是否为图片 URL
      else if (this.isImageUrl(text)) {
        message.imageUrl = text;
        message.text = '';
      }

      this.messages.push(message);
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    scrollToBottom() {
      if (this.$refs.messageContainer) {
        this.$refs.messageContainer.scrollTop = this.$refs.messageContainer.scrollHeight;
      }
    },
    // 处理表情选择
    handleEmojiSelect(data) {
      // 插入表情格式：[微笑]
      this.newMessage += `[${data.faceName}]`;
    },
    // 格式化时间戳
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      // 创建一个 Date 对象
      const date = new Date(timestamp);

      // 获取年月日时分秒
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份从 0 开始，所以要加 1
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');

      // 返回格式化的日期字符串
      return `${month}-${day} ${hour}:${minute}`;
    },
    // 触发文件上传
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    // 处理文件上传
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 检查文件大小（限制为10MB）
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$message.error(this.$t('chatWidget.fileTooLarge'));
        this.$refs.fileInput.value = '';
        return;
      }

      // 检查是否为图片
      if (!file.type.startsWith('image/')) {
        this.$message.error(this.$t('chatWidget.invalidFileType'));
        this.$refs.fileInput.value = '';
        return;
      }

      // 创建FormData上传图片
      const formData = new FormData();
      formData.append('file', file);

      // 先显示上传中的状态
      const uploadingMessageIndex = this.messages.length;
      this.messages.push({
        type: 'user',
        text: this.$t('chatWidget.uploading'),
        uploading: true
      });
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // 调用上传接口
      uploadImg(formData).then(res => {
        console.log('图片上传成功:', res);

        // 移除上传中的消息
        this.messages.splice(uploadingMessageIndex, 1);

        // 检查WebSocket连接状态
        if (!this.socket || !this.socket.connected) {
          this.$message.error(this.$t('chatWidget.connectionLost'));
          return;
        }

        // 获取图片URL
        const imageUrl = res.data.url || res.url;

        // 发送图片消息
        const requestId = this.generateRequestId();
        const timestamp = Date.now();

        // 添加用户消息到界面
        this.messages.push({
          type: 'user',
          text: '',
          imageUrl: imageUrl,
          timestamp: timestamp
        });

        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom();
        });

        // 通过WebSocket发送图片消息
        const messageData = {
          requestId: requestId,
          timestamp: timestamp,
          version: '1.0',
          globalUserId: this.globalUserId,
          from: this.formData.email,
          groupId: this.globalUserId,
          contentType: 4, // 4表示图片消息
          content: imageUrl,
          referenceContent: JSON.stringify({})
        };

        this.socket.emit('send', messageData, (res) => {
          if (!res) {
            console.error('图片发送失败,超时');
            this.$message.error(this.$t('chatWidget.imageSendFailed'));
          } else {
            console.log('图片发送成功:', res);
          }
        });

        // 清空文件输入
        this.$refs.fileInput.value = '';
      }).catch(err => {
        console.error('图片上传失败:', err);
        this.$message.error(this.$t('chatWidget.uploadFailed'));

        // 移除上传中的消息
        this.messages.splice(uploadingMessageIndex, 1);

        // 清空文件输入
        this.$refs.fileInput.value = '';
      });
    },
    // 评价提交成功
    handleRatingSubmit(data) {
      console.log('评价提交成功:', data);
      this.rateVisible = false;
      this.sessionEnded = true;
      // 可以选择关闭会话或继续
    },
    // 显示评价弹窗
    showRateDialog() {
      this.rateVisible = true;
      this.sessionEnded = true;
      // 设置评价数据
      this.rateData = {
        rate: null,
        remark: '',
        groupUniqueId: this.globalUserId || '',
        reply: 1,
        imMessageSessionId: this.groupId || ''
      };
    },
    // 立即结束会话
    handleRatingEnd() {
      console.log('立即结束会话');
      this.rateVisible = false;
      this.sessionEnded = true;

      // 清除用户信息，要求重新登录
      this.formData = {
        name: '',
        email: '',
        phone: ''
      };

      // 删除本地保存的用户信息 cookie
      this.deleteCookie('tinggo_chat_user_info');

      // 断开WebSocket连接
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }
      this.isConnected = false;
      this.isCSAssigned = false;
      this.csUserId = null;
      this.globalUserId = null;

      this.closeChat();
    },
    // 返回聊天
    handleRatingCancel() {
      console.log('返回聊天');
      this.rateVisible = false;
      this.sessionEnded = false;
    }
  }
};
</script>

<style lang="less" scoped>
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  // z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 图一：初始按钮样式 */
.chat-button {
  background: #134176;
  color: white;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(19, 65, 118, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: #0f3059;
    box-shadow: 0 4px 12px rgba(19, 65, 118, 0.4);
  }

  .chat-button-content {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 24px;
      height: 24px;
    }

    span {
      font-size: 14px;
      font-weight: 500;
    }
  }
}

/* 图二、三、四：弹框通用样式 */
.chat-popup {
  position: fixed;
  bottom: 10px;
  right: 10px;
  width: 380px;
  height: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-popup-header {
  padding: 20px 24px;
  background: #134176;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .close-button {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    font-size: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.chat-popup-content {
  padding: 32px 28px;
}

/* 图二：欢迎弹框样式 */
.avatar-section {
  text-align: center;
  margin-bottom: 28px;

  .avatar {
    width: 88px;
    height: 88px;
    margin: 0 auto;
    background: #f0f4f8;
    border: 3px solid #134176;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.welcome-title {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
  text-align: center;
  margin: 0 0 16px 0;
}

.welcome-message {
  font-size: 15px;
  color: #718096;
  text-align: center;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.continue-btn {
  width: 100%;
  padding: 14px 20px;
  background: #134176;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #0f3059;
  }
}

/* 图三：表单样式 */
.chat-popup-form {
  .form-title {
    font-size: 20px;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 28px 0;
    text-align: center;
  }

  .chat-form {
    .el-form-item {
      margin-bottom: 20px;

      ::v-deep .el-input__inner {
        border-radius: 8px;
        border: 1px solid #dcdfe6;
        padding: 14px 18px;
        font-size: 15px;

        &:focus {
          border-color: #134176;
        }
      }
    }

    .submit-btn {
      width: 100%;
      padding: 14px 20px;
      background: #134176;
      border: none;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 500;
      margin-top: 12px;

      &:hover {
        background: #0f3059;
      }
    }
  }
}

/* 图四：聊天对话框样式 */
.chat-popup-chat {
  display: flex;
  flex-direction: column;

  .chat-popup-header {
    padding: 20px 24px;

    .chat-header-info {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 14px;

      .avatar-small {
        width: 44px;
        height: 44px;
        background: white;
        border-radius: 50%;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .chat-title {
        color: white;
        font-size: 16px;
        font-weight: 600;
      }
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 10px;

      .end-chat-button {
        background: rgba(255, 255, 255, 0.15);
        border: none;
        color: white;
        padding: 8px 14px;
        border-radius: 6px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }

      .close-button {
        background: rgba(255, 255, 255, 0.15);
        border: none;
        color: white;
        width: 36px;
        height: 36px;
        border-radius: 6px;
        font-size: 22px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    background: #f7fafc;

    .message {
      margin-bottom: 20px;

      .message-content {
        display: flex;
        gap: 12px;
        max-width: 80%;

        .avatar-message {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .message-bubble {
          padding: 14px 18px;
          border-radius: 14px;
          font-size: 15px;
          line-height: 1.5;
          word-wrap: break-word;
        }

        .message-time {
          font-size: 11px;
          color: #999;
          margin: 0 0 6px 0;
          padding: 0;
        }
      }

      &.agent {
        .message-content {
          flex-direction: row;

          .message-bubble {
            background: white;
            color: #2d3748;
            border-bottom-left-radius: 6px;
          }
        }
      }

      &.user {
        .message-content {
          flex-direction: row-reverse;
          margin-left: auto;

          .message-bubble {
            background: #134176;
            color: white;
            border-bottom-right-radius: 6px;
          }
        }
      }
    }

    .typing-indicator {
      display: flex;
      gap: 5px;
      padding: 14px 18px;

      span {
        width: 9px;
        height: 9px;
        background: #cbd5e0;
        border-radius: 50%;
        animation: typing 1.4s infinite;

        &:nth-child(2) {
          animation-delay: 0.2s;
        }

        &:nth-child(3) {
          animation-delay: 0.4s;
        }
      }
    }
  }

  @keyframes typing {

    0%,
    60%,
    100% {
      transform: translateY(0);
    }

    30% {
      transform: translateY(-4px);
    }
  }

  .chat-input-area {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    background: white;
    border-top: 1px solid #e2e8f0;
    position: relative;

    .input-actions {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    .action-btn {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: #f5f5f5;
      border: 1px solid #e2e8f0;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      flex-shrink: 0;

      &:hover {
        background: #e8e8e8;
        color: #134176;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;

        &:hover {
          background: #f5f5f5;
          color: #666;
        }
      }

      svg {
        width: 22px;
        height: 22px;
      }
    }

    ::v-deep .el-input {
      flex: 1;

      .el-input__inner {
        border-radius: 24px;
        border: 1px solid #e2e8f0;
        padding: 12px 20px;
      }

      &.is-disabled .el-input__inner {
        background-color: #f5f5f5;
        color: #999;
        cursor: not-allowed;
      }
    }

    &.session-ended {
      opacity: 0.6;
      pointer-events: none;

      .input-actions {
        pointer-events: none;
      }
    }
  }

  .common_chat_emoji-wrapper {
    display: flex;
    align-items: center;
    position: relative;

    .emoji-list-wrapper {
      position: absolute;
      bottom: 50px;
      left: 0;
      width: 350px;
      height: 210px;
      background-color: #fff;
      border: 1px solid #e6e6e6;
      padding: 10px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      border-radius: 8px;
      z-index: 100;

      .emoji-list-inner {
        width: 100%;
        height: 100%;
        overflow-y: auto;
        display: grid;
        grid-template-columns: repeat(10, 1fr);
        gap: 2px;

        .emoji-item {
          cursor: pointer;
          border-radius: 4px;
          transition: background 0.2s ease;

          &:hover {
            background: #f5f5f5;
          }
        }
      }
    }
  }

  .image-message {
    .message-image {
      max-width: 250px;
      max-height: 250px;
      border-radius: 8px;
      cursor: pointer;

      ::v-deep .el-image__inner {
        border-radius: 8px;
      }
    }

    .image-error {
      width: 200px;
      height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f5;
      border-radius: 8px;
      font-size: 32px;
      color: #ccc;
    }
  }
}
</style>

<!-- 全局表情样式 (不能使用 scoped,否则 v-html 生成的元素无法应用样式) -->
<style lang="less">
.common_chat_emoji-wrapper-global {
  .qqemoji {
    display: inline-block;
    width: 28px;
    height: 28px;
    text-indent: -999em;
    background: url('../../assets/qqEmoji.png') 0 0 no-repeat;
    vertical-align: middle;
    margin: 0 2px;
  }

  .qqemoji.small {
    vertical-align: middle;
    height: 24px !important;
    width: 24px;
    transform: scale(0.82);
    margin-top: -5px;
    margin-left: -3px;
  }

  .qqemoji.qqemoji0 {
    background-position: 0 0;
  }

  .qqemoji.qqemoji1 {
    background-position: -29px 0;
  }

  .qqemoji.qqemoji2 {
    background-position: -58px 0;
  }

  .qqemoji.qqemoji3 {
    background-position: -87px 0;
  }

  .qqemoji.qqemoji4 {
    background-position: -116px 0;
  }

  .qqemoji.qqemoji5 {
    background-position: -145px 0;
  }

  .qqemoji.qqemoji6 {
    background-position: -174px 0;
  }

  .qqemoji.qqemoji7 {
    background-position: -203px 0;
  }

  .qqemoji.qqemoji8 {
    background-position: -232px 0;
  }

  .qqemoji.qqemoji9 {
    background-position: -261px 0;
  }

  .qqemoji.qqemoji10 {
    background-position: -290px 0;
  }

  .qqemoji.qqemoji11 {
    background-position: -319px 0;
  }

  .qqemoji.qqemoji12 {
    background-position: -348px 0;
  }

  .qqemoji.qqemoji13 {
    background-position: -377px 0;
  }

  .qqemoji.qqemoji14 {
    background-position: -406px 0;
  }

  .qqemoji.qqemoji15 {
    background-position: 0 -29px;
  }

  .qqemoji.qqemoji16 {
    background-position: -29px -29px;
  }

  .qqemoji.qqemoji17 {
    background-position: -58px -29px;
  }

  .qqemoji.qqemoji18 {
    background-position: -87px -29px;
  }

  .qqemoji.qqemoji19 {
    background-position: -116px -29px;
  }

  .qqemoji.qqemoji20 {
    background-position: -145px -29px;
  }

  .qqemoji.qqemoji21 {
    background-position: -174px -29px;
  }

  .qqemoji.qqemoji22 {
    background-position: -203px -29px;
  }

  .qqemoji.qqemoji23 {
    background-position: -232px -29px;
  }

  .qqemoji.qqemoji24 {
    background-position: -261px -29px;
  }

  .qqemoji.qqemoji25 {
    background-position: -290px -29px;
  }

  .qqemoji.qqemoji26 {
    background-position: -319px -29px;
  }

  .qqemoji.qqemoji27 {
    background-position: -348px -29px;
  }

  .qqemoji.qqemoji28 {
    background-position: -377px -29px;
  }

  .qqemoji.qqemoji29 {
    background-position: -406px -29px;
  }

  .qqemoji.qqemoji30 {
    background-position: 0 -58px;
  }

  .qqemoji.qqemoji31 {
    background-position: -29px -58px;
  }

  .qqemoji.qqemoji32 {
    background-position: -58px -58px;
  }

  .qqemoji.qqemoji33 {
    background-position: -87px -58px;
  }

  .qqemoji.qqemoji34 {
    background-position: -116px -58px;
  }

  .qqemoji.qqemoji35 {
    background-position: -145px -58px;
  }

  .qqemoji.qqemoji36 {
    background-position: -174px -58px;
  }

  .qqemoji.qqemoji37 {
    background-position: -203px -58px;
  }

  .qqemoji.qqemoji38 {
    background-position: -232px -58px;
  }

  .qqemoji.qqemoji39 {
    background-position: -261px -58px;
  }

  .qqemoji.qqemoji40 {
    background-position: -290px -58px;
  }

  .qqemoji.qqemoji41 {
    background-position: -319px -58px;
  }

  .qqemoji.qqemoji42 {
    background-position: -348px -58px;
  }

  .qqemoji.qqemoji43 {
    background-position: -377px -58px;
  }

  .qqemoji.qqemoji44 {
    background-position: -406px -58px;
  }

  .qqemoji.qqemoji45 {
    background-position: 0 -87px;
  }

  .qqemoji.qqemoji46 {
    background-position: -29px -87px;
  }

  .qqemoji.qqemoji47 {
    background-position: -58px -87px;
  }

  .qqemoji.qqemoji48 {
    background-position: -87px -87px;
  }

  .qqemoji.qqemoji49 {
    background-position: -116px -87px;
  }

  .qqemoji.qqemoji50 {
    background-position: -145px -87px;
  }

  .qqemoji.qqemoji51 {
    background-position: -174px -87px;
  }

  .qqemoji.qqemoji52 {
    background-position: -203px -87px;
  }

  .qqemoji.qqemoji53 {
    background-position: -232px -87px;
  }

  .qqemoji.qqemoji54 {
    background-position: -261px -87px;
  }

  .qqemoji.qqemoji55 {
    background-position: -290px -87px;
  }

  .qqemoji.qqemoji56 {
    background-position: -319px -87px;
  }

  .qqemoji.qqemoji57 {
    background-position: -348px -87px;
  }

  .qqemoji.qqemoji58 {
    background-position: -377px -87px;
  }

  .qqemoji.qqemoji59 {
    background-position: -406px -87px;
  }

  .qqemoji.qqemoji60 {
    background-position: 0 -116px;
  }

  .qqemoji.qqemoji61 {
    background-position: -29px -116px;
  }

  .qqemoji.qqemoji62 {
    background-position: -58px -116px;
  }

  .qqemoji.qqemoji63 {
    background-position: -87px -116px;
  }

  .qqemoji.qqemoji64 {
    background-position: -116px -116px;
  }

  .qqemoji.qqemoji65 {
    background-position: -145px -116px;
  }

  .qqemoji.qqemoji66 {
    background-position: -174px -116px;
  }

  .qqemoji.qqemoji67 {
    background-position: -203px -116px;
  }

  .qqemoji.qqemoji68 {
    background-position: -232px -116px;
  }

  .qqemoji.qqemoji69 {
    background-position: -261px -116px;
  }

  .qqemoji.qqemoji70 {
    background-position: -290px -116px;
  }

  .qqemoji.qqemoji71 {
    background-position: -319px -116px;
  }

  .qqemoji.qqemoji72 {
    background-position: -348px -116px;
  }

  .qqemoji.qqemoji73 {
    background-position: -377px -116px;
  }

  .qqemoji.qqemoji74 {
    background-position: -406px -116px;
  }

  .qqemoji.qqemoji75 {
    background-position: 0 -145px;
  }

  .qqemoji.qqemoji76 {
    background-position: -29px -145px;
  }

  .qqemoji.qqemoji77 {
    background-position: -58px -145px;
  }

  .qqemoji.qqemoji78 {
    background-position: -87px -145px;
  }

  .qqemoji.qqemoji79 {
    background-position: -116px -145px;
  }

  .qqemoji.qqemoji80 {
    background-position: -145px -145px;
  }

  .qqemoji.qqemoji81 {
    background-position: -174px -145px;
  }

  .qqemoji.qqemoji82 {
    background-position: -203px -145px;
  }

  .qqemoji.qqemoji83 {
    background-position: -232px -145px;
  }

  .qqemoji.qqemoji84 {
    background-position: -261px -145px;
  }

  .qqemoji.qqemoji85 {
    background-position: -290px -145px;
  }

  .qqemoji.qqemoji86 {
    background-position: -319px -145px;
  }

  .qqemoji.qqemoji87 {
    background-position: -348px -145px;
  }

  .qqemoji.qqemoji88 {
    background-position: -377px -145px;
  }

  .qqemoji.qqemoji89 {
    background-position: -406px -145px;
  }

  .qqemoji.qqemoji90 {
    background-position: 0 -174px;
  }

  .qqemoji.qqemoji91 {
    background-position: -29px -174px;
  }

  .qqemoji.qqemoji92 {
    background-position: -58px -174px;
  }

  .qqemoji.qqemoji93 {
    background-position: -87px -174px;
  }

  .qqemoji.qqemoji94 {
    background-position: -116px -174px;
  }

  .qqemoji.qqemoji95 {
    background-position: -145px -174px;
  }

  .qqemoji.qqemoji96 {
    background-position: -174px -174px;
  }

  .qqemoji.qqemoji97 {
    background-position: -203px -174px;
  }

  .qqemoji.qqemoji98 {
    background-position: -232px -174px;
  }

  .qqemoji.qqemoji99 {
    background-position: -261px -174px;
  }

  .qqemoji.qqemoji100 {
    background-position: -290px -174px;
  }

  .qqemoji.qqemoji101 {
    background-position: -319px -174px;
  }

  .qqemoji.qqemoji102 {
    background-position: -348px -174px;
  }

  .qqemoji.qqemoji103 {
    background-position: -377px -174px;
  }

  .qqemoji.qqemoji104 {
    background-position: -406px -174px;
  }
}
</style>